import type { ListViewStatus } from "./ListView";

/**
 * ListView의 append(무한 스크롤) 전용 데이터 소스 store.
 *
 * `DataTableSource`(page 기반: 페이지 교체·total·페이지 캐시)와 별개 계층이다 — 이쪽은
 * 페이지를 받아 누적 concat 하고 hasMore로 끝을 판단한다. `useSyncExternalStore`로 snapshot을
 * 구독해 그대로 ListView props(items/status/hasMore/loadingMore/onLoadMore)에 흘려보낸다.
 * 소비처는 `onLoadPage`(필수)와 `getItemKey`(mutation 식별)만 구현한다.
 */

export type ListViewPageRequest = {
  // 이미 로드된 항목 수(이어서 받을 위치). 첫 페이지는 0.
  offset: number;
  size: number;
};

export type ListViewPageResponse<Item> = {
  // 이번 페이지 항목(누적 목록 뒤에 append 된다).
  items: readonly Item[];
  // 다음 페이지가 더 있는지. false면 이후 loadMore는 무시된다.
  hasMore: boolean;
};

export type ListViewSourceSnapshot<Item> = {
  items: readonly Item[];
  // ListView status prop에 직결: idle(초기)/loading(첫 페이지)/success(로드됨)/error(첫 로드 실패).
  status: ListViewStatus;
  errorMessage: string | null;
  hasMore: boolean;
  // 추가 페이지 이어받기 진행 중(첫 로드와 구분; status는 success 유지).
  loadingMore: boolean;
};

export type ListViewSourceOptions = {
  pageSize?: number;
  resolveErrorMessage?: (error: unknown) => string;
};

const defaultPageSize = 20;

export abstract class ListViewSource<Item> {
  private readonly listeners = new Set<() => void>();
  private readonly pageSize: number;
  private readonly resolveErrorMessage: (error: unknown) => string;
  private snapshot: ListViewSourceSnapshot<Item>;
  // 최신 요청만 snapshot에 반영하기 위한 serial. 늦게 도착한 응답·dispose 후 응답은 버린다.
  private requestSerial = 0;
  private disposed = false;

  protected constructor({
    pageSize = defaultPageSize,
    resolveErrorMessage = resolveDefaultErrorMessage,
  }: ListViewSourceOptions = {}) {
    this.pageSize = pageSize;
    this.resolveErrorMessage = resolveErrorMessage;
    this.snapshot = {
      items: [],
      status: "idle",
      errorMessage: null,
      hasMore: false,
      loadingMore: false,
    };
  }

  // 구독/스냅샷: useSyncExternalStore 계약. 재구독은 dispose 이후 재활성 진입점.
  getSnapshot = (): ListViewSourceSnapshot<Item> => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.disposed = false;
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // 첫 페이지 로드. status loading → success/error.
  initialize(): Promise<void> {
    return this.reload();
  }

  // 누적을 비우고 첫 페이지부터 다시 받는다(데이터가 바뀐 뒤 호출).
  refresh(): Promise<void> {
    return this.reload();
  }

  private reload(): Promise<void> {
    this.disposed = false;
    const requestId = ++this.requestSerial;
    this.setSnapshot({
      items: [],
      status: "loading",
      errorMessage: null,
      hasMore: false,
      loadingMore: false,
    });
    return this.fetchPage({ offset: 0, size: this.pageSize }, requestId, false);
  }

  // 다음 페이지를 받아 누적 append. 첫 로드 전·진행 중·더 없음이면 무시한다.
  loadMore(): Promise<void> {
    const current = this.snapshot;
    if (current.status !== "success" || !current.hasMore || current.loadingMore) {
      return Promise.resolve();
    }
    const requestId = ++this.requestSerial;
    this.setSnapshot({ ...current, loadingMore: true });
    return this.fetchPage({ offset: current.items.length, size: this.pageSize }, requestId, true);
  }

  private async fetchPage(
    request: ListViewPageRequest,
    requestId: number,
    append: boolean,
  ): Promise<void> {
    try {
      const response = await this.onLoadPage(request);
      // dispose 되었거나 더 늦은 요청이 시작됐으면 응답을 버린다.
      if (this.disposed || requestId !== this.requestSerial) {
        return;
      }
      const items = append ? [...this.snapshot.items, ...response.items] : [...response.items];
      this.setSnapshot({
        items,
        status: "success",
        errorMessage: null,
        hasMore: response.hasMore,
        loadingMore: false,
      });
    } catch (error) {
      if (this.disposed || requestId !== this.requestSerial) {
        return;
      }
      // 첫 로드 실패는 error 상태로 표시. 이어받기 실패는 누적 목록을 유지하고 loadingMore만 풀어
      // (hasMore 보존) 다음 시도를 허용한다.
      if (append) {
        this.setSnapshot({ ...this.snapshot, loadingMore: false });
        return;
      }
      this.setSnapshot({
        items: [],
        status: "error",
        errorMessage: this.resolveErrorMessage(error),
        hasMore: false,
        loadingMore: false,
      });
    }
  }

  // mutation: 누적 목록을 낙관적으로 갱신. hasMore/페이지 경계는 건드리지 않는다.
  replaceItem(item: Item): void {
    const key = this.getItemKey(item);
    this.setSnapshot({
      ...this.snapshot,
      items: this.snapshot.items.map((current) =>
        this.getItemKey(current) === key ? item : current,
      ),
    });
  }

  removeItem(itemKey: string): void {
    const items = this.snapshot.items.filter((current) => this.getItemKey(current) !== itemKey);
    if (items.length === this.snapshot.items.length) {
      return;
    }
    this.setSnapshot({ ...this.snapshot, items });
  }

  prependItem(item: Item): void {
    this.setSnapshot({ ...this.snapshot, items: [item, ...this.snapshot.items] });
  }

  // 구독자·진행 중 요청 무효화.
  dispose(): void {
    this.disposed = true;
    this.requestSerial += 1;
    this.listeners.clear();
  }

  // 소비처 구현: 페이지 fetch.
  protected abstract onLoadPage(request: ListViewPageRequest): Promise<ListViewPageResponse<Item>>;

  // 소비처 구현: mutation 대상 식별 키(ListView의 getKey와 동일 기준이면 된다).
  protected abstract getItemKey(item: Item): string;

  private setSnapshot(snapshot: ListViewSourceSnapshot<Item>): void {
    this.snapshot = snapshot;
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}

// generic 기본 문구는 영어다. 로케일 문구가 필요한 소비처는 resolveErrorMessage 옵션으로 주입한다.
function resolveDefaultErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return "Failed to load data.";
}
